import React, { useMemo } from "react"

import Carousel from "decentraland-gatsby/dist/components/Carousel/Carousel"
import ImgFixed from "decentraland-gatsby/dist/components/Image/ImgFixed"
import Paragraph from "decentraland-gatsby/dist/components/Text/Paragraph"
import SubTitle from "decentraland-gatsby/dist/components/Text/SubTitle"
import useAsyncMemo from "decentraland-gatsby/dist/hooks/useAsyncMemo"
import Catalyst from "decentraland-gatsby/dist/utils/api/Catalyst"
import chunk from "decentraland-gatsby/dist/utils/array/chunk"
import TokenList from "decentraland-gatsby/dist/utils/dom/TokenList"
import once from "decentraland-gatsby/dist/utils/function/once"
import { Button } from "decentraland-ui/dist/components/Button/Button"
import { useMobileMediaQuery } from "decentraland-ui/dist/components/Media/Media"
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import shuffle from "lodash.shuffle"

import jumpIn from "../../images/jump-in.svg"
import LoadingFeed from "./LoadingFeed"
import { FeedProps } from "./types"
import { ellipsis, useFeedColumnName } from "./utils"

export type Scene = {
  name: string
  description: string
  position: [number, number]
  target?: string
  image?: string
}

export type ScenesFeedProps = FeedProps & {
  scenes: Scene[]
}

export type AggregatedScene = Scene & {
  concurrence: number
}

const DEFAULT_COLUMNS = 4
const PLAZA = [
  "0,0",
  "-9,73",
  "-71,-71",
  "52,52",
  "-92,-10",
  "72,-9",
  "-71,-71",
  "52,-71",
  "-9,73",
]

type SceneData = {
  position: [number, number]
  target: string
  description: string
  title: string
  image: string
}

const fetchPOIs = once(async () => {
  const catalyst = Catalyst.get()
  const pois = await catalyst.getPOIs()
  const data = await catalyst.getEntityScenes(shuffle([...PLAZA, ...pois]))
  return data.map<SceneData>((scene) => {
    const position = (scene?.metadata?.scene?.base || "0,0")
      .split(",")
      .map(Number)
      .slice(0, 2) as [number, number]
    const target = `https://play.decentraland.org/?position=${position.join(
      ","
    )}`
    const description = scene?.metadata?.display?.description || ``
    let title = scene?.metadata?.display?.title || ""
    if (!title || title === "interactive-text") {
      title = `Parcel ${position.join(",")}`
    }

    let image = scene?.metadata?.display?.navmapThumbnail || ""
    if (image && !image.startsWith("https://")) {
      const list = scene.content || []
      const content = list.find((content) => content.file === image)
      if (content) {
        image = catalyst.getContentUrl(content.hash)
      }
    }

    if (!image || !image.startsWith("https://")) {
      image = `https://api.decentraland.org/v1/parcels/${position.join(
        "/"
      )}/map.png`
    }
    return { position, target, description, title, image }
  })
})

export default React.memo(function POIsFeed({ columns }: FeedProps) {
  const isMobile = useMobileMediaQuery()
  const columnClassName = useFeedColumnName(columns, DEFAULT_COLUMNS)
  const [scenes, state] = useAsyncMemo(fetchPOIs, [])
  const scenesGroup = useMemo<SceneData[][]>(() => {
    if (!scenes || scenes.length === 0) {
      return []
    }

    if (isMobile) {
      return [scenes]
    }

    return Array.from(chunk(scenes, columns || DEFAULT_COLUMNS))
  }, [scenes, isMobile, columns])

  if (state.loading) {
    return <LoadingFeed columns={columns} />
  }

  return (
    <Carousel className="Feed">
      {scenesGroup.map((scenes, i) => {
        return (
          <div key={`group::${i}`} className="Feed__List">
            {scenes.map((scene) => {
              return (
                <a
                  key={`scene::${scene.position}`}
                  className={TokenList.join(["Feed__Item", columnClassName])}
                  href={scene.target}
                  target="_blank"
                >
                  <div className="Feed__Item__Background" />
                  <ImgFixed
                    dimension="square"
                    src={scene.image}
                    background="black"
                  />
                  <div className="Feed__Item__Detail animated">
                    <div className="Feed__Item__TitleContainer">
                      <SubTitle>{ellipsis(scene.title, 65)}</SubTitle>
                      {!!scene.description && (
                        <Paragraph>{ellipsis(scene.description, 85)}</Paragraph>
                      )}
                    </div>
                    <div className="Feed__Item__ActionContainer">
                      <Button primary size="small">
                        JUMP IN{" "}
                        <img
                          src={jumpIn}
                          width="16"
                          height="16"
                          className="JumpInIcon"
                        />
                      </Button>
                    </div>
                  </div>
                </a>
              )
            })}
          </div>
        )
      })}
    </Carousel>
  )
})
